/**
 * Schema definitions for the learner end-of-course AI takeaway ("Your AI takeaway").
 *
 * The backend (VentureShell) generates this report automatically when a learner
 * first completes a course, persists it, and exposes it through
 * `GET /:catalogId/learning_assistants/final_summary.json`. The AI fields mirror
 * `LearnerFinalSummaryOutput` from ai_services, camelCased per NovoEd convention.
 */

export enum LearnerFinalSummaryStatus {
  Queued = 'queued',
  Processing = 'processing',
  Ready = 'ready',
  Failed = 'failed',
}

export type LearnerFinalSummaryResourceType =
  | 'lecture_page'
  | 'course_component'
  | 'aux_resource'
  | 'topic';

export type RecommendedResource = {
  title: string | null;
  reason: string;
  resourceType: LearnerFinalSummaryResourceType | string;
  lecturePageId: number | null;
  componentId: number | null;
  resourceId: number | null;
};

export type Recommendations = {
  resources: RecommendedResource[];
  topicsToExplore: string[];
};

export type StopStartContinue = {
  stop: string[];
  start: string[];
  continue: string[];
};

export type EvidenceCounts = {
  submissionsUsed: number;
  rolePlaysUsed: number;
  qnaItemsUsed: number;
};

export type LearnerFinalSummaryReport = {
  responseType?: string;
  datetime: string;
  courseSummary: string;
  learnerWorkSummary: string;
  finalTakeaway: string;
  strengths: string[];
  growthAreas: string[];
  stopStartContinue: StopStartContinue;
  recommendations: Recommendations;
  evidenceSources: string[];
  evidenceCounts: EvidenceCounts;
  confidenceNotes: string[];
};

/**
 * Shape returned by `GET /:catalogId/learning_assistants/final_summary.json`.
 * `report` is only present once `status === Ready`.
 */
export type LearnerFinalSummaryResponse = {
  status: LearnerFinalSummaryStatus | null;
  available?: boolean;
  generatedAt?: string | null;
  reportVersion?: string | null;
  report: LearnerFinalSummaryReport | null;
  errorMessage?: string | null;
};

export type LearnerFinalSummaryState = {
  status: LearnerFinalSummaryStatus | null;
  report: LearnerFinalSummaryReport | null;
  isLoading: boolean;
  errorMessage: string | null;
  /** Whether the full-screen takeover is currently visible. */
  show: boolean;
  /** Guards the one-time auto-open when the report first becomes ready. */
  autoShown: boolean;
};
